import { useEffect, useState } from 'react';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupsIcon from '@mui/icons-material/Groups';
import ChecklistIcon from '@mui/icons-material/Checklist';
import TimelineIcon from '@mui/icons-material/Timeline';
import PaidIcon from '@mui/icons-material/Paid';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PercentIcon from '@mui/icons-material/Percent';
import { crmApi } from '@/api';
import { DashboardLayout } from '@/shared/layouts/DashboardLayout';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { DashboardSkeleton } from '@/components/ui/Skeleton';
import { QuickActionGrid } from '@/components/ui/QuickActionGrid';
import { PageSection } from '@/components/ui/PageSection';
import type { CrmDashboardSummary } from '@/types/crm';
import { showApiError } from '@/utils/apiError';

const QUICK_ACTIONS = [
  { to: '/crm/leads', label: 'Leads', description: 'Capture and qualify new opportunities' },
  { to: '/crm/deals', label: 'Deals', description: 'Manage pipeline and forecast revenue' },
  { to: '/crm/contacts', label: 'Contacts', description: 'People and communication history' },
  { to: '/crm/reports', label: 'Reports', description: 'Pipeline, conversion, and activity exports' },
];

export function CrmDashboardPage() {
  const [stats, setStats] = useState<CrmDashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    crmApi
      .dashboardSummary()
      .then(setStats)
      .catch((err) => showApiError(err, 'Failed to load CRM dashboard'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout
        moduleLabel="CRM"
        title="CRM Dashboard"
        description="KPI overview — reports are under CRM → Reports"
      >
        <DashboardSkeleton kpiCount={8} />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      moduleLabel="CRM"
      title="CRM Dashboard"
      description="KPI overview — reports are under CRM → Reports"
    >
      <KpiGrid>
        <StatCard title="New leads" value={stats?.newLeads ?? '—'} icon={<GroupsIcon />} />
        <StatCard title="Open deals" value={stats?.openDeals ?? '—'} icon={<TrendingUpIcon />} />
        <StatCard title="Pipeline value" value={stats?.pipelineValue ?? '—'} icon={<ShowChartIcon />} />
        <StatCard title="Forecast revenue" value={stats?.forecastRevenue ?? '—'} icon={<PaidIcon />} />
        <StatCard title="Won revenue" value={stats?.wonRevenue ?? '—'} icon={<PaidIcon />} />
        <StatCard
          title="Conversion rate"
          value={stats?.conversionRate != null ? `${stats.conversionRate.toFixed(1)}%` : '—'}
          icon={<PercentIcon />}
        />
        <StatCard title="Activities due" value={stats?.activitiesDue ?? '—'} icon={<ChecklistIcon />} />
        <StatCard title="Total activities" value={stats?.activities ?? '—'} icon={<TimelineIcon />} />
      </KpiGrid>

      <PageSection title="Quick actions" description="Jump to common CRM workflows">
        <QuickActionGrid items={QUICK_ACTIONS} columns={2} />
      </PageSection>
    </DashboardLayout>
  );
}
